'use client';

import { useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { NodeDSL, EdgeDSL } from '../../types';
import { OperatorNode } from './nodes/OperatorNode';
import { NODE_META } from './nodeTypes';

const NODE_TYPES = Object.fromEntries(
  Object.keys(NODE_META).map((k) => [k, OperatorNode])
) as Record<string, typeof OperatorNode>;

const EDGE_DEFAULTS = {
  style: { stroke: '#a1a1aa', strokeWidth: 2 },
  type: 'smoothstep' as const,
  markerEnd: { type: MarkerType.ArrowClosed, color: '#a1a1aa', width: 14, height: 14 },
};

function dslToFlowNodes(nodes: NodeDSL[], readOnly: boolean): Node[] {
  return nodes.map((n) => {
    const type = n.type ?? 'filter';
    const pos = n.position ?? { x: 0, y: 0 };
    const x = typeof pos.x === 'number' ? pos.x : 0;
    const y = typeof pos.y === 'number' ? pos.y : 0;
    const config = (n.config?.params ? { type, params: n.config.params } : n.config) ?? { type, params: {} };
    const labelKey = NODE_META[type]?.labelKey;
    return {
      id: n.id,
      type: type in NODE_TYPES ? type : 'filter',
      position: { x, y },
      data: {
        label: (n as { label?: string }).label ?? (labelKey ? '' : type),
        config,
        status: 'idle' as const,
        readOnly,
      },
      selected: false,
    };
  });
}

function dslToFlowEdges(edges: EdgeDSL[]): Edge[] {
  return edges.map((e, i) => ({
    id: `e_${e.from}_${e.to}_${i}`,
    source: e.from,
    target: e.to,
    ...EDGE_DEFAULTS,
  }));
}

export interface WorkflowReadOnlyCanvasProps {
  nodes: NodeDSL[];
  edges: EdgeDSL[];
  onNodeSelect?: (nodeId: string, config: Record<string, unknown>) => void;
  selectedNodeId?: string | null;
  className?: string;
}

export function WorkflowReadOnlyCanvas({
  nodes,
  edges,
  onNodeSelect,
  selectedNodeId,
  className = '',
}: WorkflowReadOnlyCanvasProps) {
  const initialNodes = useMemo(
    () =>
      dslToFlowNodes(nodes, true).map((n) => ({
        ...n,
        selected: n.id === selectedNodeId,
      })),
    [nodes, selectedNodeId]
  );
  const initialEdges = useMemo(() => dslToFlowEdges(edges), [edges]);

  const [rfNodes, setRfNodes] = useNodesState(initialNodes);
  const [rfEdges, setRfEdges] = useEdgesState(initialEdges);

  useEffect(() => {
    const flowNodes = dslToFlowNodes(nodes, true).map((n) => ({
      ...n,
      selected: n.id === selectedNodeId,
    }));
    setRfNodes(flowNodes);
  }, [nodes, selectedNodeId, setRfNodes]);

  useEffect(() => {
    setRfEdges(dslToFlowEdges(edges));
  }, [edges, setRfEdges]);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const config = (node.data?.config as Record<string, unknown>) ?? {};
      onNodeSelect?.(node.id, config);
      setRfNodes((prev) =>
        prev.map((n) => ({ ...n, selected: n.id === node.id }))
      );
    },
    [onNodeSelect, setRfNodes]
  );

  return (
    <div className={`flex-1 h-full min-w-0 workflow-readonly-canvas ${className}`}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodeClick={onNodeClick}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.35, maxZoom: 1.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        selectNodesOnDrag={false}
        proOptions={{ hideAttribution: true }}
        className="bg-zinc-50 workflow-readonly-canvas__flow"
        defaultEdgeOptions={EDGE_DEFAULTS}
      >
        {/* <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#d4d4d8" /> */}
        <Controls
          className="!shadow-none !border !border-zinc-200 !rounded-xl overflow-hidden"
          showFitView
          showInteractive
        />
      </ReactFlow>
    </div>
  );
}
